import React, { useEffect, useState } from "react";
import { api, money, mediaUrl } from "@/lib/api";
import { Button, Badge } from "@/lib/ui";
import PaymentModal from "@/components/PaymentModal";

const PAY_STATUS = {
  under_review: { label: "Under Review", tone: "amber", icon: "fa-hourglass-half" },
  approved: { label: "Approved", tone: "green", icon: "fa-circle-check" },
  rejected: { label: "Rejected", tone: "red", icon: "fa-circle-xmark" },
};

export default function Subscription() {
  const [seller, setSeller] = useState(null);
  const [packages, setPackages] = useState([]);
  const [order, setOrder] = useState(null);
  const [payments, setPayments] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  const load = () => {
    api.get("/seller/me").then(({ data }) => setSeller(data));
    api.get("/seller/packages").then(({ data }) => setPackages(data));
    api.get("/seller/payments").then(({ data }) => setPayments(data)).catch(() => setPayments([]));
  };
  useEffect(() => { load(); }, []);

  if (!seller) return <p className="text-slate-400">Loading…</p>;
  const sub = seller.subscription || {};
  const daysLeft = sub.expiry ? Math.ceil((new Date(sub.expiry) - new Date()) / 86400000) : null;
  const isPaid = sub.status === "active" && sub.plan && (daysLeft == null || daysLeft > 0);
  const ownedPkgs = sub.packages || (isPaid ? [{ name: sub.plan, slots: seller.included_slots || 0 }] : []);
  const totalSlots = (seller.included_slots || 0) + (seller.additional_slots || 0);

  return (
    <div>
      <h1 className="font-head text-2xl font-extrabold text-slate-900">Subscription & Billing</h1>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400">Current Plan</p>
            <p className="font-head text-xl font-extrabold text-slate-900">{sub.plan || "No active plan"}</p>
          </div>
          <Badge tone={sub.status === "active" ? "green" : "slate"}>{sub.status || "inactive"}</Badge>
        </div>
        {daysLeft != null && (
          <p className="mt-2 text-sm text-slate-600">
            {daysLeft <= 2 ? <span className="font-bold text-amber-600">Your subscription expires in {daysLeft} days. Would you like to continue?</span>
              : <>Valid for <b>{daysLeft} days</b> more · expires {new Date(sub.expiry).toLocaleDateString()}</>}
          </p>
        )}
        {isPaid && (
          <div className="mt-4 border-t border-slate-100 pt-3" data-testid="owned-packages">
            <p className="text-xs uppercase tracking-wider text-slate-400">Your packages · <b className="text-slate-700" data-testid="total-slots">{totalSlots} product slots</b> in total</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {ownedPkgs.map((x, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-800" data-testid={`owned-pkg-${i}`}>
                  <i className="fa-solid fa-box" /> {x.name} <span className="text-orange-500">· {x.slots} slots</span>
                </span>
              ))}
              {seller.additional_slots > 0 && <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700"><i className="fa-solid fa-plus" /> {seller.additional_slots} extra slots</span>}
            </div>
            <p className="mt-2 text-xs text-slate-500">Need more? Add on any package below — its slots are added to your total and share your current expiry date.</p>
          </div>
        )}
      </div>

      <h3 className="mt-6 font-head text-lg font-bold text-slate-900">{isPaid ? "Add On a Package" : "Choose a Package"}</h3>
      <p className="text-sm text-slate-500">{isPaid ? "Start small and add more packages whenever you need extra product slots. Slots stack up." : "Every package is an annual subscription, valid for 1 year. Start with a smaller package and add on more later."}</p>
      <div className="mt-3 grid gap-4 sm:grid-cols-2">
        {packages.map((pkg) => {
          const price = pkg.amount || pkg.annual_price || 0;
          const features = (pkg.features || "").split(",").map((f) => f.trim()).filter(Boolean);
          return (
            <div key={pkg.id} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`package-${pkg.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-head text-lg font-extrabold text-slate-900">{pkg.name}{pkg.one_time && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800" data-testid={`onetime-${pkg.id}`}>Starter · one-time</span>}</p>
                <Badge tone="slate">Valid 1 year</Badge>
              </div>
              <p className="mt-2 font-head text-3xl font-extrabold text-orange-600">{money(price)}<span className="text-sm font-medium text-slate-400"> + GST</span></p>
              <p className="text-xs text-slate-400">All prices are + GST, valid for 1 year</p>
              <ul className="mt-3 space-y-1 text-sm text-slate-600">
                <li><i className="fa-solid fa-check mr-2 text-emerald-500" />{pkg.num_products ?? pkg.product_slots} product listings</li>
                {features.map((f) => <li key={f}><i className="fa-solid fa-check mr-2 text-emerald-500" />{f}</li>)}
              </ul>
              <Button className="mt-4 w-full" data-testid={`subscribe-${pkg.id}`}
                onClick={() => setOrder({ type: "subscription", plan_name: pkg.name, amount: price })}>
                {isPaid ? <><i className="fa-solid fa-plus" /> Add On · +{pkg.num_products ?? pkg.product_slots} slots</> : "Continue to Payment"}
              </Button>
            </div>
          );
        })}
      </div>

      <h3 className="mt-8 font-head text-lg font-bold text-slate-900">Payment Receipts</h3>
      <p className="text-sm text-slate-500">Your uploaded payment screenshots and their approval status.</p>
      {payments.length === 0 ? (
        <div className="mt-3 rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-400" data-testid="receipts-empty">
          <i className="fa-solid fa-receipt mb-2 block text-2xl" />No payments submitted yet.
        </div>
      ) : (
        <div className="mt-3 space-y-2" data-testid="receipts-list">
          {payments.map((p) => {
            const st = PAY_STATUS[p.status] || { label: p.status, tone: "slate", icon: "fa-circle-dot" };
            return (
              <div key={p.id} data-testid={`receipt-${p.id}`} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-3">
                {p.screenshot ? (
                  <img src={mediaUrl(p.screenshot)} alt="Payment screenshot" data-testid={`receipt-thumb-${p.id}`}
                    onClick={() => setLightbox(mediaUrl(p.screenshot))}
                    className="h-16 w-16 flex-shrink-0 cursor-pointer rounded-lg border border-slate-200 object-cover hover:opacity-80" />
                ) : (
                  <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg border border-dashed border-slate-300 text-slate-300"><i className="fa-solid fa-image" /></div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-slate-800">{p.plan_name || p.type}</p>
                  <p className="text-xs text-slate-500">{money(p.amount)} · {p.type} · {new Date(p.created_at).toLocaleString()}</p>
                </div>
                <Badge tone={st.tone}><span data-testid={`receipt-status-${p.id}`}><i className={`fa-solid ${st.icon} mr-1`} />{st.label}</span></Badge>
              </div>
            );
          })}
        </div>
      )}

      {order && <PaymentModal order={order} onClose={() => setOrder(null)} onDone={load} />}

      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setLightbox(null)} data-testid="receipt-lightbox">
          <button className="absolute right-5 top-5 text-2xl text-white/80 hover:text-white" onClick={() => setLightbox(null)} data-testid="receipt-lightbox-close"><i className="fa-solid fa-xmark" /></button>
          <img src={lightbox} alt="Payment screenshot" className="max-h-[90vh] max-w-full rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
